import { CheckCircle, ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import ButtonCmp from 'src/components/ButtonCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { ORDER_STATUS } from 'src/constants/common';
import { convertUtcToUserTimezone, getDateWithSuffixFormat } from 'src/utils/CommonFunctions';

import { useRadarData } from '../hooks/useRadarData';

const RadarMapAboveComponentNew = ({
    isShowDeliveredCard,
    setIsShowDeliveredCard,
    isInline = false,
}: {
    isShowDeliveredCard: boolean;
    setIsShowDeliveredCard: (value: boolean) => void;
    isInline?: boolean;
}) => {
    const { trackOrderDetail: order, isDetailLoading: isLoading } = useRadarData();
    const routeTextRef = useRef<HTMLParagraphElement>(null);
    // Inline (share-link mobile sheet): no Close — the Time Summary is an accordion instead,
    // collapsed by default, with the same chevron + grid-rows animation as CardCmp.
    const [isSummaryOpen, setIsSummaryOpen] = useState(false);
    // const targetDeliveryDateRef = useRef<HTMLParagraphElement>(null); // Target rows hidden — see Time Summary below

    const legs = order?.legs ?? [];
    // const firstLeg = legs[0]; // only fed the hidden Target Transit Time row (via pickupSchedule)
    const lastLeg = legs[legs.length - 1];
    const legId = order?.orderId;
    // Pickup schedule comes from the first leg, delivery schedule from the last leg
    // const pickupSchedule = firstLeg?.schedule; // only fed the hidden Target Transit Time row
    const schedule = lastLeg?.schedule;
    // Consignee timezone from the last leg for correct UTC → local conversion
    const consigneeTimezone = lastLeg?.consignee?.timezone;

    const deliveredAt = schedule?.actualDeliveryDatetime ? (convertUtcToUserTimezone(schedule.actualDeliveryDatetime, 'MMM Do YYYY [at] h:mmA', true, consigneeTimezone) as string) : '-';
    const deliveredAtHtml = deliveredAt !== '-' ? getDateWithSuffixFormat(deliveredAt) : '-';

    /* Target Delivery Date / Target Transit Time computations — hidden with their rows
       in the Time Summary below; kept commented for easy restore.

    // Format matches reference: "Monday, May 11th between 14:00 to 18:00"
    const targetDeliveryDate = (() => {
        if (!schedule?.currentDeliveryStartDate) return '-';

        const dayName = moment(schedule.currentDeliveryStartDate).format('dddd');
        const dateWithSuffix = moment(schedule.currentDeliveryStartDate).format('MMM Do');

        if (schedule?.currentDeliveryStartTime) {
            const start = moment(schedule.currentDeliveryStartTime, 'HH:mm:ss').format('HH:mm');

            if (schedule?.currentDeliveryEndTime) {
                const end = moment(schedule.currentDeliveryEndTime, 'HH:mm:ss').format('HH:mm');

                return `${dayName}, ${dateWithSuffix} between ${start} to ${end}`;
            }

            return `${dayName}, ${dateWithSuffix} at ${start}`;
        }

        return `${dayName}, ${dateWithSuffix}`;
    })();

    const targetDeliveryDateHtml = targetDeliveryDate !== '-' ? getDateWithSuffixFormat(targetDeliveryDate) : '-';

    // Combine date + time for diff so "Apr 23 at 20:00 → May 11 at 14:00" = 17 days (not 18)
    const targetTransitTime = (() => {
        if (!pickupSchedule?.currentPickupStartDate || !schedule?.currentDeliveryStartDate) return '-';

        const pickupStart = moment(`${pickupSchedule.currentPickupStartDate} ${pickupSchedule.currentPickupStartTime || '00:00:00'}`);
        const deliveryStart = moment(`${schedule.currentDeliveryStartDate} ${schedule.currentDeliveryStartTime || '00:00:00'}`);
        const days = deliveryStart.diff(pickupStart, 'days');

        return `${days} day${days !== 1 ? 's' : ''}`;
    })();
    */

    // Actual delivery — UTC → consignee timezone
    const trueDeliveryDate = schedule?.actualDeliveryDatetime ? (convertUtcToUserTimezone(schedule.actualDeliveryDatetime, 'MMM Do, YYYY, h:mm A', true, consigneeTimezone) as string) : '-';
    const trueDeliveryDateHtml = trueDeliveryDate !== '-' ? getDateWithSuffixFormat(trueDeliveryDate) : '-';

    const trueTransitTime = (() => {
        if (!schedule?.actualPickupDatetime || !schedule?.actualDeliveryDatetime) return '-';

        const start = moment.utc(schedule.actualPickupDatetime);
        const end = moment.utc(schedule.actualDeliveryDatetime);

        const transitDays = end.diff(start, 'days');
        if (transitDays > 0) return `${transitDays} day${transitDays !== 1 ? 's' : ''}`;

        const transitHours = end.diff(start, 'hours');
        if (transitHours > 0) return `${transitHours} hour${transitHours !== 1 ? 's' : ''}`;

        const transitMinutes = end.diff(start, 'minutes');
        if (transitMinutes > 0) return `${transitMinutes} minute${transitMinutes !== 1 ? 's' : ''}`;

        return '-';
    })();

    return (
        <>
            {!isLoading && ![ORDER_STATUS.BOOKED, ORDER_STATUS.DISPATCHED, ORDER_STATUS.IN_TRANSIT].includes(order?.orderStatus) && isShowDeliveredCard && (
                <div
                    className={`rounded-xl border border-utilityGray200 bg-white overflow-hidden ${
                        // Inline = share-link mobile sheet status card (Figma): 1px hairline shadow
                        isInline
                            ? 'w-full shadow-[0px_1px_1px_0px_rgba(16,24,40,0.05)]'
                            : 'shadow-lg max-lg:min-w-[320px] max-lg:max-w-[340px] lg:w-[388px] absolute max-lg:bottom-[100px] lg:bottom-4 right-4 z-10'
                    }`}
                >
                    {/* Header */}
                    <div
                        className={`flex items-center gap-x-1.5 ${isInline ? 'p-2 cursor-pointer' : 'px-2 lg:px-4 pt-2 lg:pt-4 pb-2 lg:pb-3'}`}
                        onClick={isInline ? () => setIsSummaryOpen((prev) => !prev) : undefined}
                    >
                        <div className={`rounded-full bg-utilitySuccess100 flex items-center justify-center flex-none ${isInline ? 'w-8 h-8' : 'w-[34px] h-[34px] lg:w-[48px] lg:h-[48px]'}`}>
                            <CheckCircle className={`text-fgSuccessPrimary ${isInline ? 'w-4 h-4' : 'w-5 h-5 lg:w-6 lg:h-6'}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h6 className={`text-xs font-semibold text-grayLight900 ${isInline ? 'leading-[18px]' : 'lg:text-sm'} ${isLoading ? 'custom-loading' : ''}`}>Delivery Completed!</h6>
                            <TruncateTextCmp message={legId ? `Route #${legId}${schedule?.actualDeliveryDatetime ? ` • ${deliveredAt}` : ''}` : ''} refName={routeTextRef}>
                                <p
                                    ref={routeTextRef}
                                    className={`text-[10px] text-textSecondary font-normal truncate ${isInline ? 'leading-[14px]' : 'leading-[16px] lg:text-xs lg:leading-[18px] mt-0.5'} ${
                                        isLoading ? 'custom-loading' : ''
                                    }`}
                                    dangerouslySetInnerHTML={{ __html: legId ? `Route #${legId}${schedule?.actualDeliveryDatetime ? ` • ${deliveredAtHtml}` : ''}` : '' }}
                                />
                            </TruncateTextCmp>
                        </div>
                        {isInline && <ChevronDown className={`w-4 h-4 text-textSecondary flex-none transition-transform duration-300 ${isSummaryOpen ? 'rotate-180' : 'rotate-0'}`} />}
                    </div>

                    {/* Time Summary — always open on desktop; accordion when inline */}
                    <div style={isInline ? { display: 'grid', gridTemplateRows: isSummaryOpen ? '1fr' : '0fr', transition: 'grid-template-rows 300ms ease' } : undefined}>
                        <div className={isInline ? 'overflow-hidden' : ''}>
                            <div className="border-t border-utilityGray200 px-2 lg:px-4 pt-2 lg:pt-3">
                                <p className="text-[12px] font-semibold text-textSecondary uppercase mb-2 lg:mb-4">Time Summary</p>
                                <div className="flex flex-col">
                                    {/* Target Delivery Date + Target Transit Time rows hidden per request —
                                kept commented for easy restore if the target columns come back.
                            <div className="flex items-center justify-between gap-x-2 mb-2 lg:mb-3 border-b border-utilityGray200 pb-2 lg:pb-3">
                                <p className="text-xs text-textSecondary font-normal flex-none">Target Delivery Date</p>
                                <TruncateTextCmp message={targetDeliveryDate !== '-' ? targetDeliveryDate : ''} refName={targetDeliveryDateRef} truncateTextParentClassName="flex-1 min-w-0 text-right">
                                    <p
                                        ref={targetDeliveryDateRef}
                                        className={`text-xs font-medium text-grayLight900 text-right truncate ${isLoading ? 'custom-loading' : ''}`}
                                        dangerouslySetInnerHTML={{ __html: targetDeliveryDateHtml }}
                                    />
                                </TruncateTextCmp>
                            </div>
                            <div className="items-center justify-between gap-x-2 mb-2 lg:mb-3 border-b border-utilityGray200 pb-2 lg:pb-3 !hidden">
                                <p className="text-xs text-textSecondary font-normal flex-none">Target Transit Time</p>
                                <p className={`text-xs font-semibold text-fgSuccessPrimary ${isLoading ? 'custom-loading' : ''}`}>{targetTransitTime}</p>
                            </div>
                            */}
                                    <div className="flex items-center justify-between gap-x-2 mb-2 lg:mb-3 border-b border-utilityGray200 pb-2 lg:pb-3">
                                        <p className="text-xs text-textSecondary font-normal flex-none">Delivery Date</p>
                                        <p className={`text-xs font-medium text-grayLight900 ${isLoading ? 'custom-loading' : ''}`} dangerouslySetInnerHTML={{ __html: trueDeliveryDateHtml }} />
                                    </div>
                                    <div className="flex items-center justify-between gap-x-2 mb-2 lg:mb-3">
                                        <p className="text-xs text-textSecondary font-normal flex-none">Transit Time</p>
                                        <p className={`text-xs font-medium text-grayLight900 ${isLoading ? 'custom-loading' : ''}`}>{trueTransitTime}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer — desktop overlay only; the inline card collapses instead of closing */}
                    {!isInline && (
                        <div className="border-t border-utilityGray200 px-2 lg:px-4 py-2 lg:py-2.5 flex items-center justify-end">
                            <ButtonCmp className="btn-outline-primary" onClick={() => setIsShowDeliveredCard(false)}>
                                Close
                            </ButtonCmp>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default RadarMapAboveComponentNew;
